import { useEffect, useState } from 'react';
import { isWeb } from 'tamagui';

/** Blur payload from DOM or RN Web `TextInput` (`relatedTarget` may live on `nativeEvent`). */
export type MoneyMapTableRowBlurEvent = unknown;

function getBlurRelatedTarget(event: unknown): Node | null {
    if (event == null || typeof event !== 'object') {
        return null;
    }
    const e = event as {
        relatedTarget?: unknown;
        nativeEvent?: { relatedTarget?: unknown };
    };
    const related = e.relatedTarget ?? e.nativeEvent?.relatedTarget ?? null;
    if (related == null) {
        return null;
    }
    return related instanceof Node ? related : null;
}

/**
 * Web / RN Web: true when focus moves to another control still inside the same
 * money-map table row (e.g. Tab from amount to a picker). Skip persist-on-blur in that case.
 */
export function isFocusMovingWithinMoneyMapTableRow(
    tableRowRootElement: Element | null | undefined,
    event: MoneyMapTableRowBlurEvent,
): boolean {
    if (tableRowRootElement == null) {
        return false;
    }
    const related = getBlurRelatedTarget(event);
    if (related == null) {
        return false;
    }
    if (typeof tableRowRootElement.contains !== 'function') {
        return false;
    }
    return tableRowRootElement.contains(related);
}

/**
 * Web / RN Web: true while focus is inside the row container (any field or control).
 * Used for the grey row highlight on take-home pay and current assets tables.
 *
 * `tableRowRootElement` must be the row’s root DOM node (from a ref callback). That node
 * is where we attach `focusin` / `focusout` to detect focus-within; on native it stays unused.
 */
export function useMoneyMapTableRowFocusWithin(
    tableRowRootElement: Element | null,
): boolean {
    const [within, setWithin] = useState(false);

    useEffect(() => {
        if (!isWeb || tableRowRootElement == null) {
            setWithin(false);
            return;
        }
        const el = tableRowRootElement as HTMLElement;
        if (typeof el.addEventListener !== 'function') {
            return;
        }

        const onFocusIn = () => {
            setWithin(true);
        };
        const onFocusOut = (event: FocusEvent) => {
            const rt = event.relatedTarget as Node | null;
            if (rt != null && el.contains(rt)) {
                return;
            }
            setWithin(false);
        };

        el.addEventListener('focusin', onFocusIn);
        el.addEventListener('focusout', onFocusOut);

        return () => {
            el.removeEventListener('focusin', onFocusIn);
            el.removeEventListener('focusout', onFocusOut);
        };
    }, [tableRowRootElement]);

    return within;
}
